import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { Button } from "react-bootstrap";

// Components
import Loader from "../../../components/Loader";
import Layout from "../../../components/layout/Layout";

// React Icons
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

// Store
import {
  loginWithEmailAndPassword,
  reset,
} from "../../../redux/auth/authSlice";
import { Helmet } from "react-helmet";
import {
  decryptPassword,
  deleteCookie,
  encryptPassword,
  readCookie,
} from "common/FunctionUtils";
import CheckboxUI from "components/CheckboxUI";
import { DiscoverInvozio } from "../DiscoverInvozio";
import ReactivateModal from "./reactivateModal";
import { LOCALES } from "common/constants";

const LoginWithEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const data = new URLSearchParams(location.search);
  const isVerified = data.get("isverified");

  const [passVisible, setPassVisibile] = useState(false);
  const [credSaved, setCredSaved] = useState(false);
  const [toggleReactivate, settoggleReactivate] = useState(false);
  const [inactiveAccount, setInactiveAccount] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  const { isLoading, isError, message } = useSelector((state) => state.auth);

  const formValues = getValues();
  useEffect(() => {
    return () => dispatch(reset());
  }, [dispatch]);

  useEffect(() => {
    if (isError) {
      toast.error(t(message));
      dispatch(reset());
    }
  }, [isError, message, dispatch, t]);

  const onSubmit = async (values) => {
    const response = await dispatch(
      loginWithEmailAndPassword({
        email: values.email,
        password: values.password,
      })
    );

    if (response?.payload === "account_deactivated") {
      settoggleReactivate(true);
    }

    if (response?.payload === "inactive_account") {
      settoggleReactivate(true);
      setInactiveAccount(true);
    }

    if (response && typeof response.payload === "object") {
      toast.success(t("Logged in successfully"));
    }
  };
  let language = localStorage.getItem("i18nextLng");

  const handleSaveLoginCred = (e) => {
    if (!e.target.checked) {
      deleteCookie("cred");
      setCredSaved(false);
    } else {
      const { email, password } = getValues();
      const encryptedPassword = encryptPassword(password);
      if (email?.length > 0 && password?.length > 0) {
        const expirationDate = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 3);
        document.cookie = `cred=${email}:${encryptedPassword}; expires=${expirationDate.toUTCString()}; path=/`;
        setCredSaved(true);
      }
    }
  };

  useEffect(() => {
    if (document.cookie) {
      const getSSN = readCookie("cred");
      if (getSSN) {
        const splitCred = getSSN.split(":");
        const email = splitCred[0];
        const password = splitCred[1];
        const decryptedPassword = decryptPassword(password);
        setValue("email", email);
        setValue("password", decryptedPassword);
        setCredSaved(true);
      }
    }
  }, [setValue]);

  useEffect(() => {
    if (isVerified) {
      toast.success(
        t(
          "Your account has been successfully verified. Thank you for your prompt action."
        )
      );
    }
  }, [isVerified]);

  // /auth/collect-response/3fa9b1ef-0a88-4301-a89c-0c66aabc6ad9?lang=de

  return (
    <Layout
      className="new_sign_up new_margin_header"
      hidefooter
      headerColor="secondary"
    >
      <Helmet>
        <title>{t("Log in to your Invozio account or sign up today")}</title>
        <meta
          name="description"
          content={t(
            "Log in to your Invozio account quickly and securely with your email. Manage invoices, payroll, and your freelance administration smoothly all in one place."
          )}
        />
        {language === LOCALES.EN_US ? (
          <link rel="canonical" href="https://invozio.com/emaillogin" />
        ) : (
          <link rel="canonical" href="https://invozio.com/e-postinloggning" />
        )}
      </Helmet>
      {isLoading && <Loader />}
      <div className="logn_pge_dsgn w-100">
        <div className="signin">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <DiscoverInvozio />
              </div>

              <div className="col-md-6">
                <div className="bg-light">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <h1 className="text-dark new_headColor">{t("Sign in")}</h1>
                    <label className="email_mns">{t("Email")}</label>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder={t("Email")}
                      required
                      {...register("email", {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t("Invalid email address"),
                        },
                      })}
                    />
                    <div>
                      <label className="email_mns">{t("Password")}</label>
                      <div className="input-group mb-3">
                        <input
                          type={passVisible ? "text" : "password"}
                          className="form-control mt-0"
                          placeholder={t("Password")}
                          required
                          {...register("password")}
                        />
                        <div className="input-group-append pr-3  login_eyeIcon">
                          {passVisible ? (
                            <AiOutlineEyeInvisible
                              className="m-auto cursor-pointer"
                              size={24}
                              onClick={() => setPassVisibile(false)}
                            />
                          ) : (
                            <AiOutlineEye
                              className="m-auto cursor-pointer"
                              size={24}
                              onClick={() => setPassVisibile(true)}
                            />
                          )}
                        </div>

                        <div className="text-right w-100 mt-2 mb-0 d-flex justify-content-between mb-1">
                          <div className={`checkbox-wrapper-37 d-flex`}>
                            <input
                              type="checkbox"
                              name="checkbox"
                              id="terms-checkbox-38"
                              checked={credSaved ? true : false}
                              onChange={(e) => handleSaveLoginCred(e)}
                            />
                            <label
                              htmlFor="terms-checkbox-38"
                              className="terms-label mr-2"
                            >
                              <CheckboxUI />
                            </label>
                            <span className="mb-1">{t("Remember me")}</span>
                          </div>

                          <Link
                            className="how_calculate_link"
                            to={
                              language === LOCALES.EN_US
                                ? `/forgot-password?lang=${LOCALES.EN_US}`
                                : `/glomt-ditt-losenord?lang=${LOCALES.SV_SE}`
                            }
                            style={{ textDecoration: "underline" }}
                          >
                            {t("Forgot Password?")}
                          </Link>
                        </div>
                      </div>
                      {Object.keys(errors).length > 0 && (
                        <div className="text-center w-100">
                          <p className="text-danger">
                            {errors[Object.keys(errors)[0]].message}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="text-cenetr">
                      <Button
                        type="submit"
                        className="mt-4  filled new_theme_btn"
                        size="lg"
                      >
                        {t("Sign in")}
                      </Button>
                    </div>

                    <div className="seprator my-4">
                      <p className="text-center">{t("OR")}</p>
                    </div>
                    <div className="btns d-flex align-items-center justify-content-center mb-3">
                      <Button
                        className="mt-0  filled new_theme_btn"
                        size="lg"
                        onClick={() =>
                          navigate(
                            language === LOCALES.EN_US
                              ? `/login?lang=${LOCALES.EN_US}`
                              : `/logga-in?lang=${LOCALES.SV_SE}`
                          )
                        }
                      >
                        {t("Mobile Bank Id")}
                      </Button>
                      {/* <Button
                        className="ml-4"
                        variant="outline-primary bg-light"
                        size="lg"
                        onClick={() => navigate('/idlogin')}
                      >
                        {t("Bank Id")}
                      </Button> */}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReactivateModal
        toggleReactivate={toggleReactivate}
        settoggleReactivate={settoggleReactivate}
        setInactiveAccount={setInactiveAccount}
        inactiveAccount={inactiveAccount}
        formValues={formValues}
      />
    </Layout>
  );
};

export default LoginWithEmail;
